import React from "react";

import cx from "classnames";
import { el, setAttr } from "redom";

import { cloneNode, type DOMComponent, setStyles } from "~/lib/dom";
import { clearTooltip, isTeams, setTooltip } from "~/lib/utils";

import useActionItemJump, { jumpToOriginalBlock } from "./useActionItemJump";

import styles from "./css/LinkedItemText.module.css";

interface Props {
	hasAccessToOriginalNote: boolean;
	disablePopup?: boolean;
	disableLink?: boolean;
	originalBlockId: string | undefined;
	originatingNoteId: string;
	inNoteEditor?: boolean;
}

export function linkedItemTooltip(hasAccessToOriginalNote: boolean) {
	return setTooltip(
		hasAccessToOriginalNote
			? "Go to and edit the original action item"
			: "You don’t have access to the original action item",
		"bottom right",
		true,
		true,
		hasAccessToOriginalNote ? "11.667em" : "12.5em",
	);
}

export default function LinkedItemText({
	hasAccessToOriginalNote,
	originalBlockId,
	originatingNoteId,
	disablePopup,
	disableLink,
	children,
	inNoteEditor,
}: React.PropsWithChildren<Props>) {
	const jumpToActionItem = useActionItemJump(originalBlockId, originatingNoteId, isTeams);

	if (inNoteEditor) {
		// This case helps with text wrapping
		return (
			<span
				role="button"
				className={cx(styles.linkedItemContent, { [styles.disabled]: !hasAccessToOriginalNote })}
				onClick={disableLink ? undefined : jumpToActionItem}
				{...(disablePopup ? {} : linkedItemTooltip(hasAccessToOriginalNote))}
			>
				{children}
			</span>
		);
	}

	return (
		<button
			disabled={!hasAccessToOriginalNote}
			className={cx(styles.linkedItemContent, { [styles.disabled]: !hasAccessToOriginalNote })}
			onClick={disableLink ? undefined : jumpToActionItem}
			{...(disablePopup ? {} : linkedItemTooltip(hasAccessToOriginalNote))}
		>
			{children}
		</button>
	);
}

export interface ProseLinkedItemTextData {
	disablePopup: boolean;
	disableClick: boolean;
	hasAccessToOriginalNote: boolean;
	isTeams: boolean;
	originalBlockId: string;
	originatingNoteId: string;
	content: Node;
}

export class ProseLinkedItemText implements DOMComponent<ProseLinkedItemTextData> {
	el: HTMLElement;
	data?: ProseLinkedItemTextData;

	static template = el("span", { role: "button", class: styles.linkedItemContent, contenteditable: "false" });

	constructor(data: ProseLinkedItemTextData) {
		this.el = cloneNode(ProseLinkedItemText.template);
		this.el.onclick = this.jumpToActionItem;
		this.update(data);
	}

	update(data: ProseLinkedItemTextData) {
		if (data.content.parentElement !== this.el) {
			if (this.el.firstChild) {
				this.el.replaceChild(data.content, this.el.firstChild);
			} else {
				this.el.append(data.content);
			}
		}

		if (
			data.disablePopup !== this.data?.disablePopup ||
			// The tooltip is showing and the original note access changed
			(!this.data?.disablePopup && data.hasAccessToOriginalNote !== this.data?.hasAccessToOriginalNote)
		) {
			clearTooltip(this.el);
			if (!data.disablePopup) {
				const tooltipProps = linkedItemTooltip(data.hasAccessToOriginalNote);
				setAttr(this.el, tooltipProps);
				if (tooltipProps.style) setStyles(this.el, tooltipProps.style);
			}
		}

		this.el.classList.toggle(styles.disabled, !data.hasAccessToOriginalNote);

		this.data = data;
	}

	private jumpToActionItem = (e: MouseEvent) => {
		if (!this.data || this.data.disableClick) return;
		jumpToOriginalBlock(this.data?.originalBlockId, this.data?.originatingNoteId, e.metaKey);
	};
}
